/**
 * FormResponseModal — a "window" onto one questionnaire submission's answers,
 * opened from a clickable line in the communication timeline (CommsThread) when
 * a patient completes a texted/emailed study questionnaire. Fetches the
 * label-resolved answers through the recruiter-only, PHI-audited endpoint
 * (GET /form-responses/:id → crmForms.getResponseDetail) on open, so each view
 * is logged rather than embedding PHI in the timeline row. The same answers are
 * still reachable from SendFormCard's inline "View answers" — this is an
 * additional surface, not a replacement.
 *
 * Answers that drove the eligibility outcome are highlighted (red = met an
 * exclusion rule, yellow = met a needs-review rule) from the response's stored
 * eligibility_reasons — the verdict the forms engine recorded at completion —
 * so a recruiter can jump straight to the answers that need their attention.
 */
import { useEffect, useRef, useState } from 'react';
import { ClipboardList, Loader2, X } from 'lucide-react';
import api from '../../services/api';
import { FormResponseDetail } from '../types';
import { relTime } from '../lib';
import { eligibilityBadge, reasonKinds } from './shared';
import ReviewAnswerGrid, { ReviewItem, isBlankAnswer } from './ReviewAnswerGrid';
import { Z } from './ui';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Could not load answers';

interface Props {
  /** The form_responses id to load; null keeps the modal closed. */
  responseId: string | null;
  onClose: () => void;
  /** Phase-7 masking: redact answer VALUES (PHI) while keeping labels/structure. */
  mask?: boolean;
}

export default function FormResponseModal({ responseId, onClose, mask = false }: Props) {
  const [detail, setDetail] = useState<FormResponseDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guards a slow fetch from rendering under a different response after the
  // recruiter closes + reopens on another timeline line.
  const reqRef = useRef<string | null>(null);

  useEffect(() => {
    if (!responseId) {
      setDetail(null);
      setError(null);
      return;
    }
    reqRef.current = responseId;
    setDetail(null);
    setError(null);
    setLoading(true);
    api
      .get(`/api/crm/form-responses/${responseId}`)
      .then((res) => {
        if (reqRef.current !== responseId) return;
        setDetail(res.data?.response || null);
      })
      .catch((e: any) => {
        if (reqRef.current !== responseId) return;
        setError(errMsg(e));
      })
      .finally(() => {
        if (reqRef.current === responseId) setLoading(false);
      });
  }, [responseId]);

  // Esc closes, matching the click-outside affordance.
  useEffect(() => {
    if (!responseId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [responseId, onClose]);

  if (!responseId) return null;

  const badge = detail?.eligibility_status ? eligibilityBadge(detail.eligibility_status) : null;
  // question_key -> 'exclude' | 'review' for per-answer highlighting (exclude
  // wins). The server sends the verdict on each answer — derived from the
  // response's own form version when it was never scored (in-progress, or an
  // agent record completed from a partial answer set), so a partial
  // questionnaire still marks the answers that tripped a rule. The stored
  // eligibility_reasons is the fallback for a server that predates that field.
  const kinds = reasonKinds(detail?.eligibility_reasons);

  // Normalize each answer for the shared review grid. Multi-select answers render
  // as checked chips (mirroring the questionnaire's checklist) EXCEPT when the
  // answer is flagged — there the red/amber highlight pill on the joined value
  // must win, so the recruiter still sees which selection drove the outcome.
  const items: ReviewItem[] = (detail?.answers || []).map((a) => {
    const highlight = a.eligibility_kind ?? kinds.get(a.question_key) ?? null;
    const chips =
      a.type === 'multiple_choice' && !highlight && !isBlankAnswer(a.display)
        ? a.display.split(', ').map((s) => s.trim()).filter(Boolean)
        : undefined;
    return { key: a.question_key, label: a.label, value: a.display, highlight, chips };
  });

  return (
    <div
      className={`fixed inset-0 ${Z.modalCeiling} flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4`}
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-t-xl bg-white shadow-xl dark:bg-gray-800 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Questionnaire answers"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-4 dark:border-gray-700">
          <div className="min-w-0">
            <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
              <ClipboardList className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
              <span className="truncate">{detail?.form_title || 'Questionnaire answers'}</span>
            </h3>
            <p className="mt-0.5 text-xs text-gray-400">
              {detail?.completed_at
                ? `Completed ${relTime(detail.completed_at)}`
                : detail?.started_at
                ? `Started ${relTime(detail.started_at)}`
                : 'Questionnaire submission'}
              {/* §6.6c provenance: who actually filled this record. */}
              {detail?.source === 'agent' && (
                <span className="ml-2 inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-medium text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                  Filled by the AI agent from the text conversation
                </span>
              )}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {badge && (
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${badge.cls}`}>
                {badge.label}
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>
          ) : detail && detail.answers.length > 0 ? (
            <ReviewAnswerGrid items={items} mask={mask} />
          ) : detail ? (
            <p className="text-sm text-gray-400">No answers recorded.</p>
          ) : (
            <p className="text-sm text-gray-400">Could not load answers.</p>
          )}
        </div>
      </div>
    </div>
  );
}
